import { ResolvedRegion } from './models';
import {
    GEO_IP_CACHE_KEY,
    GEO_IP_CACHE_EXPIRY_KEY,
    GEO_IP_CACHE_DURATION_MS,
    US_REGION_COUNTRIES,
    AMERICAS_TIMEZONES
} from './constants';
import { getAppCheckTokenHeaders } from '@/firebase';
import { BASE_URL_EU } from '@/config';

interface GeoLocationResponse {
    data: {
        country?: {
            isoCode?: string;
            name?: string;
        };
    };
}

interface RetryConfig {
    maxAttempts: number;
    baseDelayMs: number;
    maxDelayMs: number;
    timeoutMs: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxAttempts: 3,
    baseDelayMs: 1000,
    maxDelayMs: 5000,
    timeoutMs: 5000
};

export function getCachedGeoIpRegion(): ResolvedRegion | null {
    const expiry = localStorage.getItem(GEO_IP_CACHE_EXPIRY_KEY);
    if (!expiry || Date.now() > parseInt(expiry, 10)) {
        return null;
    }
    const cached = localStorage.getItem(GEO_IP_CACHE_KEY);
    return cached === 'us' || cached === 'eu' ? cached : null;
}

function cacheGeoIpRegion(region: ResolvedRegion): void {
    localStorage.setItem(GEO_IP_CACHE_KEY, region);
    localStorage.setItem(
        GEO_IP_CACHE_EXPIRY_KEY,
        String(Date.now() + GEO_IP_CACHE_DURATION_MS)
    );
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function calculateBackoffDelay(
    attempt: number,
    baseDelayMs: number,
    maxDelayMs: number
): number {
    // Exponential backoff with jitter
    const exponentialDelay = baseDelayMs * Math.pow(2, attempt);
    const jitter = Math.random() * 0.3 * exponentialDelay; // Add up to 30% jitter
    return Math.min(exponentialDelay + jitter, maxDelayMs);
}

async function fetchWithTimeout(
    url: string,
    timeoutMs: number,
    headers?: Record<string, string>
): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(url, {
            signal: controller.signal,
            headers
        });
        return response;
    } finally {
        clearTimeout(timeoutId);
    }
}

async function fetchGeoIpWithRetry(
    config: RetryConfig = DEFAULT_RETRY_CONFIG
): Promise<ResolvedRegion> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < config.maxAttempts; attempt++) {
        try {
            const headers = await getAppCheckTokenHeaders();
            const response = await fetchWithTimeout(
                `${BASE_URL_EU}/v3/geo/location`,
                config.timeoutMs,
                headers
            );

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`
                );
            }

            const data = (await response.json()) as GeoLocationResponse;
            const countryCode = data.data?.country?.isoCode;

            if (!countryCode) {
                throw new Error('Invalid response: missing country isoCode');
            }

            const region: ResolvedRegion = US_REGION_COUNTRIES.includes(
                countryCode
            )
                ? 'us'
                : 'eu';

            return region;
        } catch (error) {
            lastError =
                error instanceof Error ? error : new Error(String(error));

            // Don't retry on abort (timeout) if it's the last attempt
            if (attempt < config.maxAttempts - 1) {
                const delay = calculateBackoffDelay(
                    attempt,
                    config.baseDelayMs,
                    config.maxDelayMs
                );
                console.warn(
                    `Geo location detection attempt ${attempt + 1} failed, retrying in ${Math.round(delay)}ms:`,
                    lastError.message
                );
                await sleep(delay);
            }
        }
    }

    console.error(
        `Geo location detection failed after ${config.maxAttempts} attempts:`,
        lastError?.message
    );
    return 'eu'; // Default to EU after all retries exhausted
}

export async function detectRegionFromGeoIp(): Promise<ResolvedRegion> {
    // Check cache first
    const cachedRegion = getCachedGeoIpRegion();
    if (cachedRegion) {
        return cachedRegion;
    }

    const region = await fetchGeoIpWithRetry();

    // Cache the result
    cacheGeoIpRegion(region);

    return region;
}

export function detectRegionFromTimezone(): ResolvedRegion {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // America/* covers US, Canada, Mexico, Central & South America, Caribbean
        // US/* is legacy format for US timezones
        // Also check specific Pacific/Atlantic timezones for Americas territories
        if (
            timezone.startsWith('America/') ||
            timezone.startsWith('US/') ||
            AMERICAS_TIMEZONES.has(timezone)
        ) {
            return 'us';
        }
        return 'eu';
    } catch {
        return 'eu';
    }
}
